import { MenuItem, notify, useConfirmModal } from '@affine/component';
import { SeatAddonService } from '@affine/core/modules/cloud';
import {
  type Member,
  WorkspaceMembersService,
  WorkspacePermissionService,
} from '@affine/core/modules/permissions';
import { WorkspaceQuotaService } from '@affine/core/modules/quota';
import { Permission, WorkspaceMemberStatus } from '@affine/graphql';
import { useI18n } from '@affine/i18n';
import { useLiveData, useService } from '@toeverything/infra';
import { useCallback, useMemo } from 'react';

export interface MemberOperationItem {
  label: string;
  onClick: () => void;
  show: boolean;
  disabled?: boolean;
  loading?: boolean;
  variant?: 'primary' | 'secondary' | 'error';
}

export const useMemberOperationItems = ({
  member,
  isOwner,
  isAdmin,
  openAssignModal,
  goToTeamBilling,
  onRoleChangeStart,
  onRoleChangeEnd,
  pendingRoleChange,
}: {
  member: Member;
  isOwner: boolean;
  isAdmin: boolean;
  openAssignModal: () => void;
  goToTeamBilling: () => void;
  onRoleChangeStart?: (permission: Permission) => void;
  onRoleChangeEnd?: () => void;
  pendingRoleChange?: Permission | null;
}): MemberOperationItem[] => {
  const t = useI18n();
  const membersService = useService(WorkspaceMembersService);
  const permission = useService(WorkspacePermissionService).permission;
  const isTeam = useLiveData(permission.isTeam$);
  const seatAddonService = useService(SeatAddonService);
  const workspaceQuotaService = useService(WorkspaceQuotaService);
  const { openConfirmModal } = useConfirmModal();

  const openRemoveConfirmModal = useCallback(
    (successNotify: { title: string; message: string }) => {
      openConfirmModal({
        title: t['com.streamlain.payment.member.team.remove.confirm.title'](),
        description:
          t['com.streamlain.payment.member.team.remove.confirm.description'](),
        confirmText:
          t[
            'com.streamlain.payment.member.team.remove.confirm.confirm-button'
          ](),
        cancelText:
          t['com.streamlain.payment.member.team.remove.confirm.cancel'](),
        confirmButtonOptions: {
          variant: 'error',
        },
        onConfirm: () =>
          membersService
            .revokeMember(member.id)
            .then(result => {
              if (result) {
                notify.success({
                  title: successNotify.title,
                  message: successNotify.message,
                });
                membersService.members.revalidate();
                workspaceQuotaService.quota.revalidate();
                seatAddonService.seatAddon.revalidate();
              }
            })
            .catch(error => {
              notify.error({
                title: 'Operation failed',
                message: error.message,
              });
            }),
      });
    },
    [
      member,
      membersService,
      openConfirmModal,
      seatAddonService,
      t,
      workspaceQuotaService,
    ]
  );

  const handleAssignOwner = useCallback(() => {
    openAssignModal();
  }, [openAssignModal]);

  const handleRevoke = useCallback(() => {
    openRemoveConfirmModal({
      title: t['com.streamlain.payment.member.team.revoke.notify.title'](),
      message: t['com.streamlain.payment.member.team.revoke.notify.message']({
        name: member.name || member.email || member.id,
      }),
    });
  }, [openRemoveConfirmModal, member, t]);

  const handleApprove = useCallback(() => {
    membersService
      .approveMember(member.id)
      .then(result => {
        if (result) {
          notify.success({
            title:
              t['com.streamlain.payment.member.team.approve.notify.title'](),
            message: t[
              'com.streamlain.payment.member.team.approve.notify.message'
            ]({
              name: member.name || member.email || member.id,
            }),
          });
          membersService.members.revalidate();
          workspaceQuotaService.quota.revalidate();
          seatAddonService.seatAddon.revalidate();
        }
      })
      .catch(error => {
        notify.error({
          title: 'Operation failed',
          message: error.message,
        });
      });
  }, [member, membersService, seatAddonService, t, workspaceQuotaService]);

  const handleDecline = useCallback(() => {
    openRemoveConfirmModal({
      title: t['com.streamlain.payment.member.team.decline.notify.title'](),
      message: t['com.streamlain.payment.member.team.decline.notify.message']({
        name: member.name || member.email || member.id,
      }),
    });
  }, [member, openRemoveConfirmModal, t]);

  const handleRemove = useCallback(() => {
    openRemoveConfirmModal({
      title: t['com.streamlain.payment.member.team.remove.notify.title'](),
      message: t['com.streamlain.payment.member.team.remove.notify.message']({
        name: member.name || member.email || member.id,
      }),
    });
  }, [member, openRemoveConfirmModal, t]);

  const changeMemberRole = useCallback(
    (
      nextPermission: Permission,
      messageKey:
        | 'com.streamlain.payment.member.team.change.reader.notify.message'
        | 'com.streamlain.payment.member.team.change.admin.notify.message'
        | 'com.streamlain.payment.member.team.change.editor.notify.message'
    ) => {
      if (pendingRoleChange) {
        return;
      }
      onRoleChangeStart?.(nextPermission);
      membersService
        .adjustMemberPermission(member.id, nextPermission)
        .then(result => {
          if (result) {
            membersService.members.updateMemberPermission(
              member.id,
              nextPermission
            );
            notify.success({
              title:
                t['com.streamlain.payment.member.team.change.notify.title'](),
              message: t[messageKey]({
                name: member.name || member.email || member.id,
              }),
            });
            membersService.members.revalidate();
          }
        })
        .catch(error => {
          notify.error({
            title: 'Operation failed',
            message: error.message,
          });
        })
        .finally(() => {
          onRoleChangeEnd?.();
        });
    },
    [
      member.email,
      member.id,
      member.name,
      membersService,
      onRoleChangeEnd,
      onRoleChangeStart,
      pendingRoleChange,
      t,
    ]
  );

  const handleChangeToReader = useCallback(() => {
    changeMemberRole(
      Permission.Reader,
      'com.streamlain.payment.member.team.change.reader.notify.message'
    );
  }, [changeMemberRole]);

  const handleChangeToAdmin = useCallback(() => {
    changeMemberRole(
      Permission.Admin,
      'com.streamlain.payment.member.team.change.admin.notify.message'
    );
  }, [changeMemberRole]);

  const handleChangeToEditor = useCallback(() => {
    changeMemberRole(
      Permission.Editor,
      'com.streamlain.payment.member.team.change.editor.notify.message'
    );
  }, [changeMemberRole]);

  const handleRetryPayment = useCallback(() => {
    openConfirmModal({
      title: t['com.streamlain.payment.member.team.retry-payment.title'](),
      description:
        t[
          `com.streamlain.payment.member.team.retry-payment.${isOwner ? 'owner' : 'admin'}.description`
        ](),
      confirmText:
        t[
          isOwner
            ? 'com.streamlain.payment.member.team.retry-payment.update-payment'
            : 'Got it'
        ](),
      confirmButtonOptions: {
        variant: 'primary',
      },
      onConfirm: isOwner ? goToTeamBilling : undefined,
      cancelText: t['Cancel'](),
      cancelButtonOptions: {
        style: {
          visibility: isOwner ? 'visible' : 'hidden',
        },
      },
    });
  }, [goToTeamBilling, isOwner, openConfirmModal, t]);

  const operationButtonInfo = useMemo(() => {
    const isRoleMutableMember =
      (member.status === WorkspaceMemberStatus.Accepted ||
        member.status === WorkspaceMemberStatus.Pending) &&
      !member.isGuest;
    const canChangeLowerRole =
      isRoleMutableMember &&
      ((isOwner && member.permission !== Permission.Owner) ||
        (isAdmin &&
          member.permission !== Permission.Owner &&
          member.permission !== Permission.Admin));

    return [
      {
        label: t['com.streamlain.payment.member.team.retry-payment'](),
        onClick: handleRetryPayment,
        show: member.status === WorkspaceMemberStatus.NeedMoreSeat,
        variant: 'secondary' as const,
      },
      {
        label: t['com.streamlain.payment.member.team.approve'](),
        onClick: handleApprove,
        show: member.status === WorkspaceMemberStatus.UnderReview,
        variant: 'primary' as const,
      },
      {
        label: t['com.streamlain.payment.member.team.approve'](),
        onClick: handleRetryPayment,
        show: member.status === WorkspaceMemberStatus.NeedMoreSeatAndReview,
        variant: 'secondary' as const,
      },
      {
        label: t['com.streamlain.payment.member.team.decline'](),
        onClick: handleDecline,
        show:
          (isAdmin || isOwner) &&
          (member.status === WorkspaceMemberStatus.UnderReview ||
            member.status === WorkspaceMemberStatus.NeedMoreSeatAndReview),
        variant: 'error' as const,
      },
      {
        label: t['com.streamlain.payment.member.team.revoke'](),
        onClick: handleRevoke,
        show:
          (isAdmin || isOwner) &&
          [
            WorkspaceMemberStatus.NeedMoreSeat,
            WorkspaceMemberStatus.NeedMoreSeatAndReview,
            WorkspaceMemberStatus.Pending,
          ].includes(member.status),
        variant: 'error' as const,
      },
      {
        label: t['com.streamlain.payment.member.team.remove'](),
        onClick: handleRemove,
        show:
          (isOwner && member.status === WorkspaceMemberStatus.Accepted) ||
          (isAdmin &&
            member.status === WorkspaceMemberStatus.Accepted &&
            member.permission !== Permission.Owner &&
            member.permission !== Permission.Admin),
        variant: 'error' as const,
      },
      {
        label: t['com.streamlain.payment.member.team.change.reader'](),
        onClick: handleChangeToReader,
        show: canChangeLowerRole && member.permission !== Permission.Reader,
        disabled: Boolean(pendingRoleChange),
        loading: pendingRoleChange === Permission.Reader,
        variant: 'secondary' as const,
      },
      {
        label: t['com.streamlain.payment.member.team.change.editor'](),
        onClick: handleChangeToEditor,
        show: canChangeLowerRole && member.permission !== Permission.Editor,
        disabled: Boolean(pendingRoleChange),
        loading: pendingRoleChange === Permission.Editor,
        variant: 'secondary' as const,
      },
      {
        label: t['com.streamlain.payment.member.team.change.admin'](),
        onClick: handleChangeToAdmin,
        show:
          Boolean(isTeam) &&
          isOwner &&
          isRoleMutableMember &&
          member.permission !== Permission.Owner &&
          member.permission !== Permission.Admin,
        disabled: Boolean(pendingRoleChange),
        loading: pendingRoleChange === Permission.Admin,
        variant: 'secondary' as const,
      },
      {
        label: t['com.streamlain.payment.member.team.assign'](),
        onClick: handleAssignOwner,
        show: false,
        variant: 'secondary' as const,
      },
    ];
  }, [
    handleApprove,
    handleAssignOwner,
    handleChangeToAdmin,
    handleChangeToEditor,
    handleChangeToReader,
    handleDecline,
    handleRemove,
    handleRetryPayment,
    handleRevoke,
    isAdmin,
    isOwner,
    isTeam,
    member.isGuest,
    member.permission,
    member.status,
    pendingRoleChange,
    t,
  ]);

  return operationButtonInfo;
};

export const MemberOptions = ({
  member,
  isOwner,
  isAdmin,
  openAssignModal,
  goToTeamBilling,
}: {
  member: Member;
  isOwner: boolean;
  isAdmin: boolean;
  openAssignModal: () => void;
  goToTeamBilling: () => void;
}) => {
  const operationButtonInfo = useMemberOperationItems({
    member,
    isOwner,
    isAdmin,
    openAssignModal,
    goToTeamBilling,
  });

  return (
    <>
      {operationButtonInfo.map(item =>
        item.show ? (
          <MenuItem
            key={item.label}
            data-testid={`settings-member-option-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
            onSelect={item.onClick}
          >
            {item.label}
          </MenuItem>
        ) : null
      )}
    </>
  );
};
